import { useState } from "react";
import { useNavigate } from "react-router-dom";
import useVendorStore from "../HomePage/useVendorStore";
import { GRN_DISPUTE_ISSUE_TYPE, UPDATE_SELLER_STATUS_ENTITY_TYPE } from "utils/constants/others";
import { strings } from "utils/constants/strings";

/**
 * Raise-dispute form state, validation and submit, shared by the mobile page and the desktop drawer.
 * `restored` pre-fills the form ({ description, itemIssues }); `onNeedEmail(formState)` is called when the
 * seller profile has no email so the caller can run its own verify-email flow.
 */
export default function useGrnDisputeLogic({ grn, grnItems, source, purchaseOrderId, grnId, restored, onNeedEmail }) {
  const navigate = useNavigate();
  const getSellerProfile = useVendorStore((state) => state.getSellerProfile);
  const setPendingDisputePayload = useVendorStore((state) => state.setPendingDisputePayload);

  // Dispute form state — initialised from restored state when returning from email verification
  const [disputeIssueDescription, setDisputeIssueDescription] = useState(
    restored?.description ?? ""
  );
  const [disputeItemIssues, setDisputeItemIssues] = useState(
    restored?.itemIssues ?? {}
  );
  const [submitDisputeLoading, setSubmitDisputeLoading] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [descriptionError, setDescriptionError] = useState("");
  const [itemErrors, setItemErrors] = useState({});

  const handleClaimChange = (itemId, field) => (val) => {
    setDisputeItemIssues((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: val,
      },
    }));
    // Clear per-item error when user edits a claim for that item
    setItemErrors((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  // Returns { valid: true, issues } on success, or { valid: false } after setting
  // the appropriate error state when any validation rule is violated.
  const validateHandleSubmitDispute = () => {
    if (!disputeIssueDescription || disputeIssueDescription.trim().length === 0) {
      setDescriptionError(strings.grnDescriptionError);
      return { valid: false };
    }
    setDescriptionError("");

    const issues = Object.entries(disputeItemIssues).reduce(
      (acc, [grnItemId, v]) => {
        if (v.claimedGrnQty) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY,
            claimed_value: String(v.claimedGrnQty),
          });
        }
        if (v.claimedGstRate) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE,
            claimed_value: String(v.claimedGstRate),
          });
        }
        if (v.claimedGrnPrice) {
          acc.push({
            grn_item_id: Number(grnItemId),
            issue_type: GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT,
            claimed_value: String(v.claimedGrnPrice),
          });
        }
        return acc;
      },
      []
    );

    if (issues.length === 0) {
      setDescriptionError(strings.grnNoClaimError);
      return { valid: false };
    }

    const newItemErrors = {};
    for (const issue of issues) {
      const val = Number(issue.claimed_value);
      if (val <= 0) {
        newItemErrors[issue.grn_item_id] = strings.grnClaimMustBePositive;
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem && val === Number(grnItem.grn_quantity)) {
          newItemErrors[issue.grn_item_id] = strings.grnQtyDisputeEqualActual;
        }
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem && val === Number(grnItem.gst_rate)) {
          newItemErrors[issue.grn_item_id] = strings.gstRateDisputeEqualActual;
        } else if (val >= 50) {
          newItemErrors[issue.grn_item_id] = strings.grnGstClaimMax;
        }
      } else if (issue.issue_type === GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT) {
        const grnItem = grnItems.find((i) => i.id === issue.grn_item_id);
        if (grnItem) {
          if (val === grnItem.grn_price_per_unit) {
            newItemErrors[issue.grn_item_id] = strings.priceDisputeEqualActual;
          } else {
            const maxAllowed = 2 * grnItem.grn_price_per_unit;
            if (val > maxAllowed) {
              newItemErrors[issue.grn_item_id] = strings.grnPriceClaimMax;
            }
          }
        }
      }
    }
    if (Object.keys(newItemErrors).length > 0) {
      setItemErrors(newItemErrors);
      return { valid: false };
    }
    setItemErrors({});
    return { valid: true, issues };
  };

  const handleSubmitDispute = async () => {
    const validation = validateHandleSubmitDispute();
    if (!validation.valid) return;
    const { issues } = validation;

    const grnNumber = grn?.grn_number;
    if (!grnNumber) return;

    const payload = {
      entity_id: Number(grnId),
      entity_type: UPDATE_SELLER_STATUS_ENTITY_TYPE.PO_GRN_MAPPING,
      seller_status: "IN_DISPUTE",
      account_id: Number(window.localStorage.getItem("outlet_id")),
      grn_dispute: {
        issue_description: disputeIssueDescription,
        issues,
      },
    };

    setSubmitDisputeLoading(true);

    // ── Email check: fetch the latest seller profile and inspect email ──
    // We call getSellerProfile() which updates the Zustand store, then read
    // the current value directly from getState() to avoid stale closures.
    try {
      await getSellerProfile();
    } catch (error) {
      setSubmitDisputeLoading(false);
      setErrorMsg(error?.response?.data?.error?.message || strings.grnSomethingWentWrong);
      setShowToast(true);
      return;
    }

    // Read fresh state directly via getState() to avoid React stale-closure issue.
    const currentEmail = useVendorStore.getState().sellerProfile?.user_email ?? "";

    if (currentEmail === "") {
      // Email is missing — pause submission and let the caller collect + verify an email.
      setSubmitDisputeLoading(false);
      onNeedEmail?.({ disputeIssueDescription, disputeItemIssues });
      return;
    }

    // Email present — hand the dispute payload to the store (single source of
    // truth also read by Invoice.jsx) and let the Invoice page's existing
    // upload + confirm flow handle document upload and updateSellerStatus.
    setSubmitDisputeLoading(false);
    setPendingDisputePayload(payload);
    navigate(invoicePath);
  };

  const invoicePath =
    source === "all"
      ? `/grn/invoice/all/${purchaseOrderId}/${grnId}`
      : `/grn/invoice/pending/${purchaseOrderId}/${grnId}`;


  const disputeAllowedMap =
    grn?.vendor_grn_dispute_details?.vendor_grn_dispute_allowed
      ?.item_dispute_allowed_map || {};

  const disputeableItems = grnItems.filter((item) => {
    const allowed = disputeAllowedMap[item.id];
    return (
      allowed &&
      Array.isArray(allowed.allowed_issue_types) &&
      allowed.allowed_issue_types.length > 0
    );
  });

  const showPriceDisputeNotAllowedBanner = grnItems.some((item) => {
    const allowed = disputeAllowedMap[item.id];
    return (
      allowed &&
      Array.isArray(allowed.allowed_issue_types) &&
      !allowed.allowed_issue_types.includes(GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT)
    );
  });

  return {
    disputeIssueDescription,
    setDisputeIssueDescription,
    disputeItemIssues,
    setDisputeItemIssues,
    submitDisputeLoading,
    showToast,
    setShowToast,
    errorMsg,
    descriptionError,
    setDescriptionError,
    itemErrors,
    handleClaimChange,
    handleSubmitDispute,
    disputeAllowedMap,
    disputeableItems,
    showPriceDisputeNotAllowedBanner,
    invoicePath,
  };
}
